'use client';
import { useTranslation } from '@/i18n/client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import JDIProductCard1 from '@/components/productCard/components/type1';
import JDIProductCard from '@/components/productCard/components/type5';
import { fetchBestSellerRankProducts, fetchRecommendCategoryData } from '@/apis/apiBestSeller';
import {
  CartIconColorType,
  HoverSkuNameColorType,
  ProductState,
} from '@/components/productCard/index.interface';
import { getTopTag } from '../utils';
import BestSellerSubSidebar from './components/BestSellerSubSidebar';
import styles from './page.module.scss';
import ArrowRightIcon from '@/assets/icons/arrow-right-new.svg';
import { EXPOSURE_RECORD, sendClickTrack, sendPv } from '@/tracks/31846';
import { useInViewport } from 'ahooks';
import { debounce } from 'lodash';

export interface IBestSellerSubPageClientProps {
  bestSellerProductData: any;
  sidebarData: any;
  localInfo: any;
  sequenceId: string;
  recommendData: any;
  sidebarResMain: any;
  parentSequenceId: string;
  parentcategoryName: string;
  moreRecommendedTitles: string;
}

export default function BestSellerSubPageClient({
  bestSellerProductData,
  sidebarData,
  sidebarResMain,
  sequenceId,
  localInfo,
  recommendData,
  parentSequenceId,
  parentcategoryName,
  moreRecommendedTitles,
}: IBestSellerSubPageClientProps) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState(0);
  const [datas, setDatas] = useState(bestSellerProductData?.data);
  const [recommendDataset, setRecommendDataset] = useState(recommendData?.data);
  const [currentSeqId, setCurrSeqId] = useState(sequenceId);
  // is client
  const [isClient, setIsClient] = useState<boolean>(false);
  // totalPage
  const [totalPage, setTotalPage] = useState<number>(recommendData?.pageSize);
  // current Page
  const [currentPage, setCurrentPage] = useState<number>(recommendData?.currentPage);
  // loading
  const [loading, setLoading] = useState<boolean>(false);
  // Last page
  // const [lastPage, setLastPage] = useState<boolean>(currentPage >= totalPage);
  const [lastPage, setLastPage] = useState<boolean>(false);

  const sentinelRef = useRef<HTMLElement>(null);
  const [isVisible, ratio] = useInViewport(sentinelRef.current);

  useEffect(() => {
    sendPv.Activity_RankDetail({
      rankId: sequenceId || '-100',
      rankName: bestSellerProductData?.data?.categoryTabName || '-100',
      scoreNum: bestSellerProductData?.data?.viewedStr || '-100',
      type: bestSellerProductData?.data?.type || '-100',
      cateLevel: bestSellerProductData?.data?.level || '-100',
      tabname: bestSellerProductData?.data?.categoryTabName || '-100',
    });
    setIsClient(true);
  }, []);
  useEffect(() => {
    if (isVisible) {
      if (currentPage < totalPage) {
        debouncedHandleScroll();
      }
    }
  }, [isVisible]);

  // // -------------- scroll handler -------------------- //
  const handleScrollImmediate = useCallback(async () => {
    if (currentPage >= totalPage) {
      return;
    }

    setLoading(true);

    try {
      const nextPage = currentPage + 1;
      const result = await fetchRecommendCategoryData({
        currentPage: nextPage,
        sequenceId: sequenceId,
      });

      if (result.success && result.data.data.data) {
        setRecommendDataset((prev: any) => [...prev, ...result.data.data.data]);
        setCurrentPage(nextPage);
        setLastPage(currentPage >= totalPage);
      }
    } catch (error) {
      console.error('加载更多数据失败：', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, totalPage]);

  const debouncedHandleScroll = useMemo(
    () => debounce(handleScrollImmediate, 200),
    [handleScrollImmediate],
  );
  // -------------- fetch new data according to sequenceID -------------------- //
  async function handleFetch(sequenceId: string) {
    const res = await fetchBestSellerRankProducts({ currentPage: 1, sequenceId: sequenceId });
    const newData = res?.data?.data || {};
    setDatas(newData);
    setCurrSeqId(sequenceId);
  }
  // -------------- fetch new recommend data according to sequence id -------------------- //
  async function handleFetchRecommendData(sequenceId: string) {
    setRecommendDataset([]);
    const recommendRes = await fetchRecommendCategoryData({
      sequenceId: sequenceId,
      currentPage: 1,
    });
    const newRecommenData = recommendRes?.data?.data?.data || [];
    setRecommendDataset(newRecommenData);
    setCurrSeqId(sequenceId);
  }
  return (
    <>
      <div className={styles.innerContainer} id='best-seller-container'>
        <div className={styles.sidebar}>
          <BestSellerSubSidebar
            sidebarData={sidebarData}
            sidebarResMain={sidebarResMain}
            bestSellerProductData={bestSellerProductData}
            sequenceId={currentSeqId}
            parentSequenceId={parentSequenceId}
            parentcategoryName={parentcategoryName}
            setSequenceId={setCurrSeqId}
            handleFetch={handleFetch}
            handleFetchRecommendData={handleFetchRecommendData}
          />
        </div>
        <div className={styles.mainContentContainer}>
          <div className={styles.containerHeader}>
            <h2>{datas?.categoryTabName}</h2>
            <div className={styles.amountViewed}>
              <span className={styles.viewedText}>{datas?.viewedStr}</span>
            </div>
          </div>
          <div className={styles.cardBox}>
            {datas?.productBestSellerRanks &&
              datas?.productBestSellerRanks.map((item: any, index: number) => {
                return (
                  <JDIProductCard
                    key={index}
                    productData={
                      getProductData(
                        item,
                        index,
                        datas,
                        localInfo?.currencyCode,
                        localInfo?.languageCode,
                      ) as any
                    }
                  />
                );
              })}
          </div>
        </div>
      </div>

      <h2 className={styles.dividerText}>{moreRecommendedTitles}</h2>

      <div className={styles.recommendCardContainer}>
        {recommendDataset &&
          recommendDataset?.map((recomendedList: any, index: number) => {
            return (
              <div key={index} className={styles.card}>
                <div className={styles.cardHeader}>
                  <h3> {recomendedList.categoryTabName}</h3>
                  <a
                    href={`/promo/best-seller/${recomendedList.sequenceId}`}
                    exptag={'exp|' + EXPOSURE_RECORD.Activity_RankDetail_RankCard_LookMore_Expo}
                    onClick={() =>
                      sendClickTrack.Activity_RankDetail_RankCard_LookMore({
                        rankId: recomendedList?.sequenceId || '-100',
                        rankName: recomendedList?.categoryTabName || '-100',
                        type: `${recomendedList?.type}` || '-100',
                        scoreNum: recomendedList?.viewedStr || '-100',
                        index: index || -100,
                        cateLevel: recomendedList?.level || '-100',
                        tabName: recomendedList?.categoryTabName || '-100',
                      })
                    }
                    data-exptag-config='{"useClick": false, "stay_duration": 0, "repeated":false, "area_rate": 0.01}'
                    data-exptag-json-param={JSON.stringify({
                      rankid: recomendedList?.sequenceId || '-100',
                      rank_name: recomendedList?.categoryTabName || '-100',
                      cateLevel: recomendedList?.level || '-100',
                      scoreNum: recomendedList?.viewedStr || '-100',
                      type: `${recomendedList?.type}` || '-100',
                    })}
                  >
                    <p>{recomendedList?.viewedStr}</p>
                    <ArrowRightIcon className={styles.arrowIcon} />
                  </a>
                </div>
                <div className={styles.cardContent}>
                  {recomendedList &&
                    recomendedList?.productBestSellerRanks
                      ?.slice(0, 3)
                      ?.map((sku: any, idx: number) => {
                        return (
                          <JDIProductCard1
                            key={idx}
                            productData={
                              getRecommendProductData(
                                sku,
                                idx,
                                recomendedList,
                                localInfo?.currencyCode,
                                localInfo?.languageCode,
                              ) as any
                            }
                          />
                        );
                      })}
                </div>
                <div className={styles.cardMask}></div>
              </div>
            );
          })}
        <div ref={sentinelRef as any} style={{ height: '1px', width: '100%' }}></div>
        <div className={styles.end}>
          {loading ? t('bestSeller.loading') : lastPage && t('bestSeller.recommend.endTxt')}
        </div>
      </div>
    </>
  );
}

const getProductData = (
  product: any,
  index: number,
  item: any,
  currencyCode: string,
  languageCode: string,
) => {
  const trackParam = {
    skuid: product?.skuId || '-100',
    firprice: parseFloat(product?.realPrice) || -100,
    firpricetype: '1',
    secprice: parseFloat(product?.originalPriceV2) || -100,
    secpricetype: '1',
    rankId: item?.sequenceId || '-100',
    rankName: item?.categoryTabName || '-100',
    rankNum: product?.rank || '-100',
    type: item?.type || '-100',
    scoreNum: item?.viewedStr || '-100',
    tabName: item?.categoryTabName || '-100',
  } as any;
  return {
    type: 5,
    viewedTip: product?.viewedStr,
    skuId: product?.skuId,
    skuName: product?.skuName,
    skuImg: product?.skuImage,
    shouldShowCart: true,
    shouldShowPrice: true,
    shouldShowMarketing: true,
    brandName: product?.brandName,
    realPrice: product?.realPrice,
    originalPrice: product?.originalPriceV2,
    originalPriceTips: product?.originalPriceDesc,
    currency: currencyCode,
    languageCode,
    productState:
      product.wareInStock === '0'
        ? ProductState.OUT_OF_STICK
        : product?.stockStatus === 1
          ? ProductState.IN_STOCK
          : ProductState.OUT_OF_STICK,
    imgTips: {
      position: 'topLeft',
      tipDom: getTopTag({ index, width: 30, height: 39 }),
    },
    promotions: product?.labels,
    // ---- styles ------ //
    containerStyle: {
      display: 'flex',
      flexDirection: 'column',
      maxWidth: '194px',
    },

    trackingPointParams: {
      productExpConfig: {
        event_id: EXPOSURE_RECORD.Activity_RankDetail_Product_Expo,
        config: {
          useClick: false,
          stay_duration: 0.3,
          repeated: false,
          area_rate: 0.5,
        },
        json_param: trackParam,
      },

      productClickPoint: () => sendClickTrack.Activity_RankDetail_Product(trackParam),
      cartClickPoint: () => sendClickTrack.Activity_RankDetail_AddToCart(trackParam),
    },
  };
};

const getRecommendProductData = (
  product: any,
  index: number,
  item: any,
  currencyCode: string,
  languageCode: string,
) => {
  const trackParams = {
    skuid: product?.skuId || '-100',
    firprice: parseFloat(product?.realPrice) || -100,
    firpricetype: '1',
    secprice: parseFloat(product?.originalPriceV2) || -100,
    secpricetype: '1',
    index: index || -100,
    rankId: item?.sequenceId || '-100',
    rankName: item?.categoryTabName || '-100',
    cateLevel: product?.level || '-100',
    type: item?.type || '-100',
    tab_name: item?.categoryTabName || '-100',
    scoreNum: item?.viewedStr || '-100',
    rankNum: index || '-100',
    rankPos: index || '-100',
    rank_no: index || '-100',
  } as any;

  return {
    type: 1,
    skuId: product?.skuId,
    skuName: product?.skuName,
    skuImg: product?.skuImage,
    shouldShowCart: true,
    shouldShowPrice: true,
    shouldShowMarketing: true,
    brandName: product?.brandName,
    realPrice: product?.realPrice,
    originalPrice: product?.originalPriceV2,
    originalPriceTips: product?.originalPriceDesc,
    currency: currencyCode,
    languageCode,
    isOutOfStock:
      product.wareInStock === '0'
        ? ProductState.OUT_OF_STICK
        : product?.stockStatus !== 1
          ? ProductState.OUT_OF_STICK
          : false,
    cartIconColor: CartIconColorType.BLACK,
    isUnderline: true,
    hoverSkuNameColor: HoverSkuNameColorType.BLACK,
    priceAndCartStyle: {
      alignItems: 'flex-start',
    },
    imgTips: {
      position: 'topLeft',
      tipDom: getTopTag({ index, width: 40, height: 52 }),
    },
    // ---- styles ------ //
    containerStyle: {
      display: 'flex',
      flexDirection: 'column',
      width: '175px',
      height: 'auto',
    },
    skuNameStyle: {
      fontWeight: 500,
      fontSize: '14px',
      lineHeight: '20px',
      height: '40px',
      lineClamp: 2,
      WebkitLineClamp: 2,
    },

    realPriceJDIPriceParams: {
      symbolStyle: { fontSize: '16px' },
      integerStyle: { fontSize: '24px' },
      decimalPointStyle: { fontSize: '24px' },
      decimalPlaceStyle: { fontSize: '24px' },
      containerStyle: {
        display: 'block',
        height: '32px',
        lineHeight: '26px',
        fontWeight: 700,
      },
    },
    originalPriceJDIPriceParams: {
      symbolStyle: { fontSize: '16px' },
      integerStyle: { fontSize: '16px' },
      decimalPointStyle: { fontSize: '16px' },
      decimalPlaceStyle: { fontSize: '16px' },
      containerStyle: {
        fontWeight: 500,
      },
    },
    // -------------- 埋点 -------------------- //
    trackingPointParams: {
      productExpConfig: {
        event_id: EXPOSURE_RECORD.Activity_RankDetail_RankCard_Product_Expo,
        config: {
          useClick: false,
          stay_duration: 0,
          repeated: false,
          area_rate: 0.01,
        },
        json_param: trackParams,
      },
      productClickPoint: sendClickTrack.Activity_RankDetail_RankCard_Product(trackParams),
      cartClickPoint: () => sendClickTrack.Activity_RankDetail_RankCard_Cart(trackParams),
    },
  };
};
